<template>
  <div class="contact-widget">
    <div v-if="isOpen" class="contact-backdrop" @click="closePanel"></div>

    <Transition name="contact-slide">
      <div v-if="isOpen" class="contact-panel">
        <div class="contact-panel-header">Hubungi Kami</div>

        <a
          v-for="item in contactItems"
          :key="item.label"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
          class="contact-item"
          @click="closePanel"
        >
          <span class="contact-item-label">{{ item.label }}</span>
          <span class="contact-item-value">{{ item.value }}</span>
        </a>
        <p v-if="!contactItems.length" class="contact-empty">
          Kontak belum tersedia. Coba beberapa saat lagi.
        </p>
      </div>
    </Transition>

    <button
      class="contact-toggle-btn"
      type="button"
      :aria-expanded="isOpen"
      aria-label="Buka daftar kontak dan media sosial"
      @click="togglePanel"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
      </svg>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { useConstantStore } from "~/store/constant";
import { ConstantKey } from "~/contract/enum/ConstantRelated.enum";

interface ContactItem {
  label: string;
  value: string;
  url: string;
}

const WHATSAPP_GREETING = "Halo, saya ingin bertanya mengenai produk di Vapebay.";

const constantStore = useConstantStore();

const isOpen = ref(false);
const companyPhone = ref<string>("");
const companyEmail = ref<string>("");
const companyInstagram = ref<string>("");
const companyTiktok = ref<string>("");

const togglePanel = () => {
  isOpen.value = !isOpen.value;
};

const closePanel = () => {
  isOpen.value = false;
};

/**
 * Konstanta diisi lewat panel admin, jadi bentuknya tidak selalu seragam: bisa
 * "vapebay.id", bisa "@vapebay.id". Tanda @ dibuang lebih dulu supaya tidak ikut
 * masuk dua kali ke URL profilnya.
 */
const toUsername = (rawValue: string): string => rawValue.trim().replace(/^@+/, "");

const contactItems = computed<ContactItem[]>(() => {
  const items: ContactItem[] = [];

  if (companyPhone.value) {
    const whatsappNumber = normalizePhoneNumber(companyPhone.value);
    if (whatsappNumber) {
      const queryParam = new URLSearchParams({
        phone: whatsappNumber,
        text: WHATSAPP_GREETING,
        type: "phone_number",
      }).toString();
      items.push({
        label: "WhatsApp",
        value: companyPhone.value,
        url: `https://api.whatsapp.com/send/?${queryParam}`,
      });
    }
  }

  if (companyInstagram.value) {
    const username = toUsername(companyInstagram.value);
    items.push({
      label: "Instagram",
      value: `@${username}`,
      url: `https://www.instagram.com/${username}`,
    });
  }

  if (companyTiktok.value) {
    const username = toUsername(companyTiktok.value);
    items.push({
      label: "TikTok",
      value: `@${username}`,
      url: `https://www.tiktok.com/@${username}`,
    });
  }

  if (companyEmail.value) {
    items.push({
      label: "Email",
      value: companyEmail.value,
      url: `mailto:${companyEmail.value}`,
    });
  }

  return items;
});

onMounted(async () => await nextTick(async () => {
  companyPhone.value = await constantStore.getConstant(ConstantKey.COMPANY_PHONE) || ""
  companyEmail.value = await constantStore.getConstant(ConstantKey.COMPANY_EMAIL) || ""
  companyInstagram.value = await constantStore.getConstant(ConstantKey.COMPANY_INSTAGRAM) || ""
  companyTiktok.value = await constantStore.getConstant(ConstantKey.COMPANY_TIKTOK) || ""
}))
</script>

<style scoped>
.contact-widget {
  position: fixed;
  bottom: 90px;
  right: 24px;
  z-index: 1030;
}

.contact-backdrop {
  position: fixed;
  inset: 0;
  z-index: -1;
}

.contact-toggle-btn {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(145deg, transparent 0%, rgba(0, 0, 0, 0.2) 100%), var(--theme-color, #f32e4a);
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(243, 46, 74, 0.4);
  transition: all 0.3s ease;
}

.contact-toggle-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 30px rgba(243, 46, 74, 0.6);
}

.contact-toggle-btn:active {
  transform: scale(0.95);
}

.contact-panel {
  position: absolute;
  bottom: 76px;
  right: 0;
  width: 260px;
  max-width: calc(100vw - 48px);
  padding: 8px 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.contact-panel-header {
  padding: 8px 16px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #222;
}

.contact-item {
  display: flex;
  flex-direction: column;
  padding: 8px 16px;
  text-decoration: none;
  transition: background 0.2s ease;
}

.contact-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.contact-item-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--theme-color, #f32e4a);
}

.contact-item-value {
  font-size: 12px;
  color: #666;
  word-break: break-all;
}

.contact-empty {
  margin: 0;
  padding: 4px 16px 12px;
  font-size: 12px;
  color: #666;
}

@media (max-width: 768px) {
  .contact-widget {
    right: 16px;
  }
}

@media (max-width: 576px) {
  .contact-widget {
    bottom: 140px;
  }
}

.contact-slide-enter-active,
.contact-slide-leave-active {
  transition: all 0.25s ease;
}

.contact-slide-enter-from,
.contact-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
